import { Dispatch, useContext, useEffect, useState } from "react";
import { endPoints, postData } from "../../../../utilities/networkCalls";
import { Action } from "../../../Bundles/Types";
import { CartConfigInterface, SingleRewardTier } from "../../CartTypes";
import { Button, Checkbox, FormLayout, Select, TextField } from "@shopify/polaris";
import styles from "./CartFormCards.module.css";
import { RewardBasis, RewardTypes } from "../../../Bundles/constants";
import TextEditorCart from "../../../../components/TextEditorCart/TextEditorCart";
import DeleteIcon from "../../CartIcons/DeleteIcon";
import FreeGiftReward from "./FreeGiftReward";
import NewCustomColorPicker from "../../../../components/NewCustomColorPicker/NewCustomColorPicker";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import ClickAwayListener from "../../../Bundles/BundlesComponents/ClickAwayListener/ClickAwayListener";

const CartRewardsForm = ({
    fields,
    dispatchCartConfig,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
}) => {
    const { currency } = useContext(AppConfigContext);
    const [errors, setErrors] = useState<string[]>([]);
    const [hasInteractedProduct, setHasInteractedProduct] = useState(false);
    const [markets, setMarkets] = useState<any[]>([]);
    const [selectedMarket, setSelectedMarket] = useState<string>("default");

    useEffect(() => {
        const fetchMarkets = async () => {
            const query = `
                query {
                    markets(first: 100) {
                        nodes {
                            id
                            name
                            handle
                            primary
                            currencySettings {
                                baseCurrency {
                                    currencyCode
                                }
                            }
                        }
                    }
                }
            `;
            try {
                const res = await postData({
                    url: endPoints.executeGraphql,
                    payload: { query },
                });
                if (res?.status === 200 || res?.status === 201) {
                    const marketsData = res?.data?.data?.data?.markets?.nodes || [];
                    const cleanedMarkets = marketsData.map((market: any) => ({
                        ...market,
                        id: market.id.split("/").pop(),
                    }));

                    setMarkets(cleanedMarkets);

                    setSelectedMarket(() => {
                        let market: string | undefined = "default";
                        if (fields?.reward?.tiers?.value.length) {
                            return market;
                        }

                        if (fields?.reward?.market_tiers?.value) {
                            market =
                                Object.keys(fields.reward.market_tiers.value).find(
                                    (key) => !!fields.reward.market_tiers.value[key].length,
                                ) || "default";
                        }

                        return market;
                    });
                }
            } catch (err) {
                console._log?.("Error fetching markets", err);
            }
        };
        fetchMarkets();
    }, []);

    const {
        reward: {
            show_reward_on_empty_cart,
            text_after_reward_completion,
            reward_basis,
            tiers,
            auto_add_free_gifts,
            market_tiers,
        },
        reward_design: {
            bar_background_color,
            bar_foreground_color,
            free_gift_bg_color,
            free_gift_button_bg_color,
            free_gift_button_text_color,
            reward_free_gift_text_color,
            reward_free_gift_product_title_color,
            progress_message_text_color,
            free_gift_free_label_color,
        },
    } = fields;

    const currentTiersValue =
        selectedMarket === "default" ? tiers?.value || [] : market_tiers?.value?.[selectedMarket] || [];

    useEffect(() => {
        if (tiers?.error || market_tiers?.error) {
            setHasInteractedProduct(true);
        }
    }, [tiers?.error, market_tiers?.error]);

    const handleAddTier = () => {
        if (currentTiersValue?.length < 3) {
            let newTier = {};
            const lastTier = currentTiersValue?.length ? currentTiersValue[currentTiersValue.length - 1] : null;

            if (reward_basis.value == "cart_total") {
                const baseDisc = lastTier ? lastTier.discount_value + 5 : 5;
                const baseCount = lastTier ? lastTier.count + 1000 : 1000;
                newTier = {
                    type: "discount",
                    description: baseDisc + "% discount",
                    count: baseCount,
                    discount_value: baseDisc,
                    free_products: [],
                    text_before_tier_completion: `You're {amount} away from getting ${baseDisc}% discount`,
                    show_tier_completion_text: false,
                    text_after_tier_completion: `Congrats! You've received a ${baseDisc}% discount`,
                };
            } else {
                const baseDisc = lastTier ? lastTier.discount_value + 5 : 5;
                const baseCount = lastTier ? lastTier.count + 1 : 1;
                newTier = {
                    type: "discount",
                    description: baseDisc + "% discount",
                    count: baseCount,
                    discount_value: baseDisc,
                    free_products: [],
                    text_before_tier_completion: `You're {item} items away from getting ${baseDisc}% discount`,
                    show_tier_completion_text: false,
                    text_after_tier_completion: `Congrats! You've received a ${baseDisc}% discount`,
                };
            }

            const activeTiersUpdated = [...currentTiersValue, newTier];

            if (selectedMarket === "default") {
                tiers.onChange(activeTiersUpdated);
                dispatchCartConfig({
                    payload: { tiers: activeTiersUpdated },
                    isComplex: true,
                    key: "reward",
                });
            } else {
                const updatedMarketTiers = { ...(market_tiers?.value || {}), [selectedMarket]: activeTiersUpdated };
                market_tiers.onChange(updatedMarketTiers);
                dispatchCartConfig({
                    payload: { market_tiers: updatedMarketTiers },
                    isComplex: true,
                    key: "reward",
                });
            }
        }
    };

    const handleUpdateTier = (index: any, updates: any) => {
        const updatedTiers = [...currentTiersValue];

        if (updatedTiers[index]) {
            updatedTiers[index] = {
                ...updatedTiers[index],
                ...updates,
            };
        }

        if (selectedMarket === "default") {
            tiers.onChange(updatedTiers);
            checkForErrors(updatedTiers);
            dispatchCartConfig({
                payload: { tiers: updatedTiers },
                isComplex: true,
                key: "reward",
            });
        } else {
            const updatedMarketTiers = { ...(market_tiers?.value || {}), [selectedMarket]: updatedTiers };
            market_tiers.onChange(updatedMarketTiers);
            checkForErrors(updatedTiers);
            dispatchCartConfig({
                payload: { market_tiers: updatedMarketTiers },
                isComplex: true,
                key: "reward",
            });
        }
    };

    const handleDeleteTier = (index: number) => {
        // if (currentTiersValue?.length === 1) {
        //     shopify.toast.show("At least one tier is necessary", {
        //         isError: true,
        //     });
        //     return;
        // }

        const updatedTiers = currentTiersValue.filter((_: any, i: any) => i !== index);
        checkForErrors(updatedTiers);

        if (selectedMarket === "default") {
            tiers.onChange(updatedTiers);
            dispatchCartConfig({
                payload: { tiers: updatedTiers },
                isComplex: true,
                key: "reward",
            });
        } else {
            const updatedMarketTiers = { ...(market_tiers?.value || {}), [selectedMarket]: updatedTiers };
            market_tiers.onChange(updatedMarketTiers);
            dispatchCartConfig({
                payload: { market_tiers: updatedMarketTiers },
                isComplex: true,
                key: "reward",
            });
        }
    };

    const checkForErrors = (_ts: any[] = []) => {
        const tempErr: string[] = Array(_ts.length).fill("");
        if (_ts.length) {
            _ts.forEach((t: SingleRewardTier, idx: number) => {
                if (t.type == "product" && t.free_products.length == 0) {
                    tempErr[idx] = "Free product reward is required.";
                } else {
                    tempErr[idx] = "";
                }
            });
            setErrors([...tempErr]);
        } else {
            setErrors([...tempErr]);
        }
    };

    console._log("errors[idx]", errors);

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>General Settings</div>
                <FormLayout>
                    <div className={`${styles.formRow}`}>
                        <Select
                            label={"Reward Basis"}
                            options={RewardBasis}
                            placeholder={""}
                            onChange={(val) => {
                                reward_basis.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        reward_basis: val,
                                    },
                                    isComplex: true,
                                    key: "reward",
                                });

                                const baseCount = val === "cart_total" ? 1000 : 1;

                                const updatedTiers = tiers.value.map((tier: any, index: any) => ({
                                    ...tier,
                                    count: baseCount * (index + 1),
                                    text_before_tier_completion:
                                        val === "cart_total"
                                            ? `<p>You're {amount} away from getting ${
                                                  tier.type === "discount"
                                                      ? `${tier.discount_value}% discount`
                                                      : tier.type === "product"
                                                        ? "Free Gift"
                                                        : "Free Shipping"
                                              }</p>`
                                            : `<p>You're {item} items away from getting ${
                                                  tier.type === "discount"
                                                      ? `${tier.discount_value}% discount`
                                                      : tier.type === "product"
                                                        ? "Free Gift"
                                                        : "Free Shipping"
                                              }</p>`,

                                    text_after_tier_completion:
                                        tier.type === "discount"
                                            ? `Congrats! You've received a ${tier.discount_value}% discount.`
                                            : tier.type === "product"
                                              ? "Great news! You've earned a free gift."
                                              : "Awesome! You've unlocked free shipping.",
                                }));

                                dispatchCartConfig({
                                    payload: {
                                        tiers: updatedTiers,
                                    },
                                    isComplex: true,
                                    key: "reward",
                                });
                                tiers.onChange(updatedTiers);

                                if (market_tiers?.value) {
                                    const marketKeys = Object.keys(market_tiers.value);
                                    let newMarketTiers = { ...market_tiers.value };
                                    for (let mk of marketKeys) {
                                        newMarketTiers[mk] = newMarketTiers[mk].map((tier: any, index: any) => ({
                                            ...tier,
                                            count: baseCount * (index + 1),
                                            text_before_tier_completion:
                                                val === "cart_total"
                                                    ? `<p>You're {amount} away from getting ${
                                                          tier.type === "discount"
                                                              ? `${tier.discount_value}% discount`
                                                              : tier.type === "product"
                                                                ? "Free Gift"
                                                                : "Free Shipping"
                                                      }</p>`
                                                    : `<p>You're {item} items away from getting ${
                                                          tier.type === "discount"
                                                              ? `${tier.discount_value}% discount`
                                                              : tier.type === "product"
                                                                ? "Free Gift"
                                                                : "Free Shipping"
                                                      }</p>`,

                                            text_after_tier_completion:
                                                tier.type === "discount"
                                                    ? `Congrats! You've received a ${tier.discount_value}% discount.`
                                                    : tier.type === "product"
                                                      ? "Great news! You've earned a free gift."
                                                      : "Awesome! You've unlocked free shipping.",
                                        }));
                                    }
                                    market_tiers.onChange(newMarketTiers);
                                    dispatchCartConfig({
                                        payload: {
                                            market_tiers: newMarketTiers,
                                        },
                                        isComplex: true,
                                        key: "reward",
                                    });
                                }
                            }}
                            value={reward_basis.value}
                        />
                    </div>

                    {/* <div className={`${styles.formRow} ${styles.row1}`}>
                        <Checkbox
                            label={"Enable automatic currency conversion"}
                            {...enable_automatic_currency_conversion}
                            value=""
                            helpText="Converts reward thresholds into the customer's currency based on Shopify's exchange rates."
                            checked={enable_automatic_currency_conversion.value}
                            onChange={(val) => {
                                dispatchCartConfig({
                                    payload: {
                                        enable_automatic_currency_conversion: val,
                                    },
                                    isComplex: true,
                                    key: "reward",
                                });
                                enable_automatic_currency_conversion.onChange(val);
                            }}
                        />
                    </div> */}

                    <div className={`${styles.formRow} ${styles.row1}`}>
                        <TextEditorCart
                            placeholder="Place your subtext here"
                            value={text_after_reward_completion.value}
                            onChange={(value: any) => {
                                const cleanedValue = value === "<p><br></p>" ? "" : value;
                                dispatchCartConfig({
                                    payload: {
                                        text_after_reward_completion: cleanedValue,
                                    },
                                    isComplex: true,
                                    key: "reward",
                                });
                                text_after_reward_completion.onChange(cleanedValue);
                            }}
                            title={"All Tier Completion Text"}
                            submit={text_after_reward_completion?.onBlur}
                        />
                    </div>
                </FormLayout>
            </div>

            <ClickAwayListener onClickAway={() => {}}>
                <div className={styles.designCardWrapper}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className={styles.cardLabel}>
                            Tiers for{" "}
                            {selectedMarket === "default"
                                ? "Default"
                                : markets.find((m: any) => m.id === selectedMarket)?.name || ""}
                        </div>
                        <Select
                            label="Markets"
                            labelInline
                            options={[
                                { label: `Default (${tiers.value?.length || 0})`, value: "default" },
                                ...markets.map((m: any) => ({
                                    label: `${m.name} (${market_tiers?.value?.[m.id]?.length || 0})`,
                                    value: m.id,
                                })),
                            ]}
                            onChange={(val) => setSelectedMarket(val)}
                            value={selectedMarket}
                        />
                    </div>

                    {selectedMarket === "default" && (
                        <div
                            style={{
                                padding: "10px",
                                backgroundColor: "#eaf3ff",
                                marginBottom: "15px",
                                borderRadius: "5px",
                                fontSize: "13px",
                                marginTop: "8px",
                            }}
                        >
                            <span>
                                ℹ️ Markets without specific tiers will use the tiers set in the Default section.{" "}
                            </span>
                        </div>
                    )}
                    {selectedMarket !== "default" && (
                        <div
                            style={{
                                padding: "10px",
                                backgroundColor: "#eaf3ff",
                                marginBottom: "15px",
                                borderRadius: "5px",
                                fontSize: "13px",
                                marginTop: "8px",
                            }}
                        >
                            <span>ℹ️ This market will use its specific tiers. </span>
                        </div>
                    )}

                    {currentTiersValue.map((tier: SingleRewardTier, idx: number) => (
                        <div key={idx} style={{ margin: "10px 0 15px 0" }}>
                            <h3
                                style={{
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    marginBottom: "4px",
                                }}
                            >
                                Tier {idx + 1}
                            </h3>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                }}
                            >
                                <Select
                                    label={"Reward Type"}
                                    options={RewardTypes}
                                    placeholder={""}
                                    onChange={(val) => {
                                        setHasInteractedProduct(false);
                                        handleUpdateTier(idx, {
                                            type: val,
                                            description:
                                                val === "product"
                                                    ? "Free Gift"
                                                    : val === "discount"
                                                      ? `${tier.discount_value}% Discount`
                                                      : "Free Shipping",
                                            text_after_tier_completion:
                                                val === "discount"
                                                    ? `Congrats! You've received a ${tier.discount_value}% discount.`
                                                    : val === "product"
                                                      ? "Great news! You've earned a free gift."
                                                      : "Awesome! You've unlocked free shipping.",
                                            text_before_tier_completion:
                                                reward_basis.value === "cart_total"
                                                    ? `<p>You're {amount} away from getting ${
                                                          val === "discount"
                                                              ? `${tier.discount_value}% discount`
                                                              : val === "product"
                                                                ? "Free Gift"
                                                                : "Free Shipping"
                                                      }</p>`
                                                    : `<p>You're {item} items away from getting ${
                                                          val === "discount"
                                                              ? `${tier.discount_value}% discount`
                                                              : val === "product"
                                                                ? "Free Gift"
                                                                : "Free Shipping"
                                                      }</p>`,
                                            free_products: [],
                                        });
                                    }}
                                    value={tier.type}
                                />

                                {tier.type == "discount" && (
                                    <TextField
                                        label={"Discount Value"}
                                        placeholder={""}
                                        autoComplete="off"
                                        type="number"
                                        min={0}
                                        suffix={"%"}
                                        onChange={(val) => {
                                            let numVal = +val;
                                            if (numVal > 100) {
                                                numVal = 100;
                                            }
                                            handleUpdateTier(idx, {
                                                discount_value: +numVal,
                                            });
                                        }}
                                        value={tier.discount_value.toString()}
                                    />
                                )}

                                {tier.type == "product" && (
                                    <div>
                                        <FreeGiftReward
                                            handleInputChange={handleUpdateTier}
                                            selectedTab={idx}
                                            free_gifts={tier.free_products}
                                            offerError={hasInteractedProduct && errors[idx] ? errors[idx] || "" : ""}
                                            setHasInteractedProduct={setHasInteractedProduct}
                                        />
                                    </div>
                                )}

                                <TextField
                                    label={"Reward Description"}
                                    placeholder={""}
                                    autoComplete="off"
                                    onChange={(val) => {
                                        handleUpdateTier(idx, { description: val });
                                    }}
                                    value={tier.description}
                                    showCharacterCount
                                    maxLength={20}
                                />

                                <TextField
                                    label={reward_basis.value == "cart_total" ? "Minimum Amount" : "Minimum Items"}
                                    placeholder={""}
                                    autoComplete="off"
                                    min={0}
                                    prefix={
                                        reward_basis.value == "cart_total"
                                            ? selectedMarket === "default"
                                                ? currency
                                                : markets.find((market: any) => market?.id === selectedMarket)
                                                      ?.currencySettings?.baseCurrency?.currencyCode
                                            : ""
                                    }
                                    type="number"
                                    onChange={(val) => {
                                        handleUpdateTier(idx, { count: +val });
                                    }}
                                    value={tier.count.toString()}
                                />

                                <TextEditorCart
                                    placeholder="Place your subtext here"
                                    value={tier.text_before_tier_completion}
                                    onChange={(value: any, _: any, source: string) => {
                                        if (source == "user") {
                                            const cleanedValue = value === "<p><br></p>" ? "" : value;
                                            handleUpdateTier(idx, { text_before_tier_completion: cleanedValue });
                                        }
                                    }}
                                    title={"Text Before Tier Completion"}
                                />

                                <div style={{ marginBottom: -10 }}>
                                    <Checkbox
                                        label={"Text After Tier Completion"}
                                        onChange={() => {
                                            handleUpdateTier(idx, {
                                                show_tier_completion_text: !tier?.show_tier_completion_text,
                                            });
                                        }}
                                        checked={tier?.show_tier_completion_text || false}
                                    />{" "}
                                </div>
                                {tier?.show_tier_completion_text && (
                                    <TextEditorCart
                                        placeholder="Place your subtext here"
                                        value={tier.text_after_tier_completion}
                                        onChange={(value: any, _: any, source: string) => {
                                            if (source == "user") {
                                                const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                handleUpdateTier(idx, { text_after_tier_completion: cleanedValue });
                                            }
                                        }}
                                    />
                                )}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    margin: "15px 0 10px 0",
                                }}
                            >
                                <span
                                    style={{
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "3px",
                                    }}
                                    onClick={() => handleDeleteTier(idx)}
                                >
                                    <DeleteIcon />
                                    Delete this tier
                                </span>
                            </div>

                            <div style={{ margin: "15px 0 0 0" }}>
                                <hr />
                            </div>
                        </div>
                    ))}

                    {currentTiersValue.length < 3 && (
                        <Button onClick={() => handleAddTier()} variant="primary">
                            Add tier for{" "}
                            {selectedMarket === "default"
                                ? "Default"
                                : markets.find((m: any) => m.id === selectedMarket)?.name || ""}
                        </Button>
                    )}
                </div>
            </ClickAwayListener>

            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Color</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row2}`}>
                        <NewCustomColorPicker
                            hexColor={bar_background_color.value}
                            setHexColor={(val) => {
                                bar_background_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        bar_background_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Progress Background Color"}
                            props={{
                                ...bar_background_color,
                                onBlur: () => bar_background_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={bar_foreground_color.value}
                            setHexColor={(val) => {
                                bar_foreground_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        bar_foreground_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Progress Foreground Color"}
                            props={{
                                ...bar_foreground_color,
                                onBlur: () => bar_foreground_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={reward_free_gift_text_color.value}
                            setHexColor={(val) => {
                                reward_free_gift_text_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        reward_free_gift_text_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Header"}
                            props={{
                                ...reward_free_gift_text_color,
                                onBlur: () => reward_free_gift_text_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={reward_free_gift_product_title_color.value}
                            setHexColor={(val) => {
                                reward_free_gift_product_title_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        reward_free_gift_product_title_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Product Title"}
                            props={{
                                ...reward_free_gift_product_title_color,
                                onBlur: () => reward_free_gift_product_title_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={free_gift_bg_color.value}
                            setHexColor={(val) => {
                                free_gift_bg_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        free_gift_bg_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Product Background"}
                            props={{
                                ...free_gift_bg_color,
                                onBlur: () => free_gift_bg_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={free_gift_button_bg_color.value}
                            setHexColor={(val) => {
                                free_gift_button_bg_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        free_gift_button_bg_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Button Background"}
                            props={{
                                ...free_gift_button_bg_color,
                                onBlur: () => free_gift_button_bg_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={free_gift_button_text_color.value}
                            setHexColor={(val) => {
                                free_gift_button_text_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        free_gift_button_text_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Button Text Color"}
                            props={{
                                ...free_gift_button_text_color,
                                onBlur: () => free_gift_button_text_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={progress_message_text_color.value}
                            setHexColor={(val) => {
                                progress_message_text_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        progress_message_text_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Progress Message Text Color"}
                            props={{
                                ...progress_message_text_color,
                                onBlur: () => progress_message_text_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={free_gift_free_label_color.value}
                            setHexColor={(val) => {
                                free_gift_free_label_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        free_gift_free_label_color: val,
                                    },
                                    isComplex: true,
                                    key: "reward_design",
                                });
                            }}
                            label={"Free Gift Free Label Color"}
                            props={{
                                ...free_gift_free_label_color,
                                onBlur: () => free_gift_free_label_color?.runValidation(),
                            }}
                        />
                    </div>
                </FormLayout>
            </div>

            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Advanced Settings</div>
                <FormLayout>
                    <Checkbox
                        label={"Show rewards on empty cart"}
                        {...show_reward_on_empty_cart}
                        value=""
                        checked={show_reward_on_empty_cart.value}
                        onChange={(val) => {
                            dispatchCartConfig({
                                payload: {
                                    show_reward_on_empty_cart: val,
                                },
                                isComplex: true,
                                key: "reward",
                            });
                            show_reward_on_empty_cart.onChange(val);
                        }}
                    />
                    <Checkbox
                        label={"Automatically add free gifts to cart"}
                        {...auto_add_free_gifts}
                        value=""
                        checked={auto_add_free_gifts.value}
                        onChange={(val) => {
                            dispatchCartConfig({
                                payload: {
                                    auto_add_free_gifts: val,
                                },
                                isComplex: true,
                                key: "reward",
                            });
                            auto_add_free_gifts.onChange(val);
                        }}
                    />
                </FormLayout>
            </div>
        </>
    );
};

export default CartRewardsForm;
